import * as XLSX from 'xlsx'
import type { GridImportColumn } from '../types'

const EXCEL_TYPES = new Set(['xlsx', 'xls'])
const SKIP_TYPES = new Set(['checkbox', 'seq', 'image', 'file', 'files'])

export type ParsedFileColumn = GridImportColumn & {
  /** Excel 原始表头文本 */
  header: string
}

export type ParseExcelResult = {
  rows: Record<string, unknown>[]
  /** 解析到的 field 列表（与 fileColumns 顺序一致） */
  fieldsInFile: string[]
  /** 按 Excel 表头顺序解析出的列（预览与校验以此为准） */
  fileColumns: ParsedFileColumn[]
}

/**
 * 将 Excel 表头规范为列 title / field，兼容：
 * - 后缀必填：`部门名称*`、`性别*1111111`
 * - 前缀必填：`*部门名称`、`* 部门名称`
 */
export function normalizeImportHeaderKey(key: string): string {
  return String(key ?? '')
    .replace(/^\*\s*/, '')
    .replace(/\*.*$/, '')
    .trim()
}

export function isExcelFile(file: File) {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? ''
  return EXCEL_TYPES.has(ext)
}

export function resolveFieldKey(
  raw: Record<string, unknown>,
  column: GridImportColumn
) {
  const field = column.field
  const title = column.title
  if (field && field in raw) return field
  if (title && title in raw) return title
  if (title && `${title}*` in raw) return `${title}*`
  if (title && `*${title}` in raw) return `*${title}`

  for (const key of Object.keys(raw)) {
    const normalized = normalizeImportHeaderKey(key)
    if (field && normalized === field) return key
    if (title && normalized === title) return key
  }
  return null
}

function resolveImportableColumns(columns: GridImportColumn[]) {
  return columns.filter(
    (col) =>
      col.field && col.importable !== false && !SKIP_TYPES.has(col.type ?? '')
  )
}

function readSheetMatrix(sheet: XLSX.WorkSheet): string[][] {
  const matrix = XLSX.utils.sheet_to_json<string[]>(sheet, {
    header: 1,
    defval: '',
    raw: false,
  })
  return matrix.map((row) => row.map((cell) => String(cell ?? '').trim()))
}

function isEmptyRow(cells: string[]) {
  return cells.every((cell) => !cell)
}

function matchColumnForHeader(
  header: string,
  columns: GridImportColumn[]
): GridImportColumn | null {
  const raw = { [header]: '' }
  for (const column of columns) {
    if (resolveFieldKey(raw, column)) return column
  }
  return null
}

function buildFileColumn(
  header: string,
  matched: GridImportColumn | null
): ParsedFileColumn {
  if (matched) {
    return {
      ...matched,
      header,
      title: header,
      importable: true,
    }
  }

  const normalizedTitle = normalizeImportHeaderKey(header)
  return {
    field: normalizedTitle || header,
    title: header,
    header,
    importable: true,
  }
}

export async function parseExcelFile(
  file: File,
  columns: GridImportColumn[],
  sheetName?: string
): Promise<ParseExcelResult> {
  const buffer = await file.arrayBuffer()
  const workbook = XLSX.read(buffer, { type: 'array' })
  const targetSheet =
    sheetName && workbook.SheetNames.includes(sheetName)
      ? sheetName
      : workbook.SheetNames[0]
  if (!targetSheet) {
    return { rows: [], fieldsInFile: [], fileColumns: [] }
  }

  const sheet = workbook.Sheets[targetSheet]!
  const matrix = readSheetMatrix(sheet)
  if (!matrix.length) {
    return { rows: [], fieldsInFile: [], fileColumns: [] }
  }

  const headerRow = matrix[0]!
  const configColumns = resolveImportableColumns(columns)
  const fileColumns: ParsedFileColumn[] = []
  const columnIndexes: number[] = []

  headerRow.forEach((header, index) => {
    if (!header) return
    fileColumns.push(
      buildFileColumn(header, matchColumnForHeader(header, configColumns))
    )
    columnIndexes.push(index)
  })

  if (!fileColumns.length) {
    return { rows: [], fieldsInFile: [], fileColumns: [] }
  }

  const fieldsInFile: string[] = []
  const rows: Record<string, unknown>[] = []

  for (let rowIndex = 1; rowIndex < matrix.length; rowIndex += 1) {
    const cells = matrix[rowIndex]!
    if (isEmptyRow(cells)) continue

    const row: Record<string, unknown> = {}
    fileColumns.forEach((column, columnIndex) => {
      const cellIndex = columnIndexes[columnIndex]!
      row[column.field] = cells[cellIndex] ?? ''
      if (!fieldsInFile.includes(column.field)) {
        fieldsInFile.push(column.field)
      }
    })
    rows.push(row)
  }

  return { rows, fieldsInFile, fileColumns }
}
